import React from 'react';
import { Box, Menu, MenuItem, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DotsHorizontal, HeartIcon, ShareIcon07 } from 'components/SVG';
import IconButtonWrapper from './IconButtonWrapper';
import { usePopover } from 'hooks/common';
import { SPARK_MODAL_TYPES, TARGET_VALUES } from 'constants/common';
import type { ActionOptions } from 'types/common';

type Props = {
  showShareIcon?: boolean;
  showHeartIcon?: boolean;
  showDotsIcon?: boolean;
  onOptionClick?: (
    type: keyof typeof SPARK_MODAL_TYPES | null,
    event?: React.MouseEvent<HTMLButtonElement>
  ) => void;
  menuItems?: ActionOptions[] | undefined;
  onDialogClose: () => void;
  isResourceFavourite?: boolean | undefined;
  transformMenu?: boolean;
};

function SparkOptions({
  showShareIcon = false,
  showHeartIcon = false,
  showDotsIcon = true,
  isResourceFavourite = false,

  onOptionClick,
  menuItems,
  onDialogClose,
  transformMenu,
}: Props) {
  const navigate = useNavigate();
  const { anchorEl, open, openPopover, closePopover } = usePopover();

  const handleDotsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    onOptionClick && onOptionClick(null);
    openPopover(event);
  };

  const handleClose = () => {
    onOptionClick && onOptionClick(null);
    closePopover();
    onDialogClose();
  };

  const handleMenuItemClick = (
    item: ActionOptions,
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    const e = event as React.MouseEvent<HTMLButtonElement>;
    e.preventDefault();
    e.stopPropagation();
    if (item.target === TARGET_VALUES.modal) {
      onOptionClick &&
        onOptionClick(
          item.modalSettings?.type as keyof typeof SPARK_MODAL_TYPES,
          e
        );
    } else {
      closePopover();

      if (item.target === TARGET_VALUES.self) {
        navigate(item.route);
      } else window.open(item.route, item.target);
    }
  };

  const sortedMenuItems = React.useMemo(
    () => menuItems?.sort((a, b) => a.sortOrder - b.sortOrder),
    [menuItems]
  );

  return (
    <>
      {transformMenu ? (
        <Box display='flex' flexDirection='column' width='100%'>
          {sortedMenuItems?.map((item, index) => (
            <MenuItem
              key={index}
              component={item.target === TARGET_VALUES.modal ? 'span' : 'a'}
              href={item.route}
              onClick={(event: any) => {
                handleMenuItemClick(item, event);
              }}
            >
              {item.iconURL && (
                <Box
                  component='img'
                  src={item.iconURL}
                  alt={item.label}
                  sx={{ aspectRatio: 1, width: '16px' }}
                />
              )}

              <Typography
                sx={{ marginLeft: 1 }}
                component='span'
                variant='body1'
              >
                {item.label}
              </Typography>
            </MenuItem>
          ))}
        </Box>
      ) : (
        <>
          {showShareIcon && (
            <IconButtonWrapper
              onClick={(event) => {
                onOptionClick &&
                  onOptionClick(
                    SPARK_MODAL_TYPES.share as keyof typeof SPARK_MODAL_TYPES,
                    event
                  );
              }}
              style={{ margin: 0 }}
            >
              <ShareIcon07
                stroke='var( --color-primary)'
                width='18'
                height='18'
              />
            </IconButtonWrapper>
          )}
          {showHeartIcon && (
            <IconButtonWrapper
              onClick={(event) => {
                onOptionClick &&
                  onOptionClick(
                    SPARK_MODAL_TYPES.favourite as keyof typeof SPARK_MODAL_TYPES,
                    event
                  );
              }}
              style={{ margin: 0 }}
            >
              {isResourceFavourite ? (
                <HeartIcon color='#EF5251' fill='#EF5251' />
              ) : (
                <HeartIcon />
              )}
            </IconButtonWrapper>
          )}
          {showDotsIcon && sortedMenuItems && sortedMenuItems?.length > 0 && (
            <>
              <IconButtonWrapper
                onClick={handleDotsClick}
                style={{ margin: 0 }}
              >
                <DotsHorizontal />
              </IconButtonWrapper>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {sortedMenuItems.map((item, index) => (
                  <MenuItem
                    key={index}
                    component={
                      item.target === TARGET_VALUES.modal ? 'span' : 'a'
                    }
                    href={item.route}
                    onClick={(event: any) => {
                      handleMenuItemClick(item, event);
                    }}
                  >
                    {item.iconURL && (
                      <Box
                        component='img'
                        src={item.iconURL}
                        alt={item.label}
                        sx={{
                          aspectRatio: 1,
                          width: '16px',
                          marginBottom: '2px',
                        }}
                      />
                    )}

                    <Typography
                      sx={{ marginLeft: 1 }}
                      component='span'
                      variant='body1'
                    >
                      {item.label}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </>
      )}
    </>
  );
}

export default SparkOptions;
